// V96-4-7 / ADR-0045: SOC 2 "provided by client" (PBC) request template.
//
// For each Trust Services Criterion, the requests a service auditor
// typically sends during fieldwork, written in GAC Pro's own words (no
// AICPA text, D-96-6). Auditors tailor their own list; this template lets
// pbc-service pre-fill each request with evidence already on file.
//
// `sources` says which kinds of on-file evidence can answer a request:
//   checks      — control_checks on controls that credit the criterion
//                 (reviewed control_edges), inside the audit period
//   policy_keys — published policy versions (soc2-policy-templates keys)
//                 and their acknowledgment completion
//   population_kinds — uploaded or collected populations of these kinds
//   obligations — completed recurring-obligation occurrences on crediting
//                 controls, inside the audit period
// A request with no sources is always answered by hand.
//
// Pure data — no runtime imports.
export const SOC2_PBC_TEMPLATE_VERSION = 'gacpro-soc2-pbc@1';
const RAW = {
    // ---- CC1 Control environment ------------------------------------------
    'CC1.1': [
        ['Current code of conduct and evidence personnel acknowledged it during the period', { policy_keys: ['information_security', 'hr_security'], population_kinds: ['code_of_conduct_acks'], checks: true }],
        ['Description of how conduct violations are reported and handled, with any cases raised in the period', {}],
    ],
    'CC1.2': [
        ['Board or advisory body charter and minutes of meetings in the period where security was discussed', { checks: true, obligations: true }],
    ],
    'CC1.3': [
        ['Organization chart and a list of security roles with named owners', { policy_keys: ['information_security'], checks: true }],
    ],
    'CC1.4': [
        ['Listing of new hires in the period with background-check completion dates', { population_kinds: ['background_checks', 'new_hires'], checks: true }],
        ['Security training completion records for personnel in the period', { population_kinds: ['security_training'], checks: true, obligations: true }],
        ['Job descriptions for roles with security responsibilities', { policy_keys: ['hr_security'] }],
    ],
    'CC1.5': [
        ['Evidence that personnel are held accountable for control duties (performance reviews, disciplinary process)', { policy_keys: ['hr_security'], checks: true }],
    ],
    // ---- CC2 Communication and information --------------------------------
    'CC2.1': [
        ['Inventory of systems and data sources used to operate and monitor controls', { checks: true }],
    ],
    'CC2.2': [
        ['Published security policies with acknowledgment records for the period', { policy_keys: ['information_security', 'acceptable_use'], checks: true }],
        ['How personnel report security concerns (channel and example reports)', {}],
    ],
    'CC2.3': [
        ['Customer-facing security commitments (terms, trust page, SLAs) and how changes are communicated', { checks: true }],
    ],
    // ---- CC3 Risk assessment ----------------------------------------------
    'CC3.1': [
        ['Documented business and security objectives used for the risk assessment', { policy_keys: ['risk_management'], checks: true }],
    ],
    'CC3.2': [
        ['Most recent risk assessment and risk register, with owners and treatment decisions', { policy_keys: ['risk_management'], checks: true, obligations: true }],
    ],
    'CC3.3': [
        ['Evidence that fraud risk was considered in the risk assessment', { policy_keys: ['risk_management'], checks: true }],
    ],
    'CC3.4': [
        ['Listing of significant changes in the period and how each was assessed for control impact', { population_kinds: ['changes'], checks: true }],
    ],
    // ---- CC4 Monitoring ---------------------------------------------------
    'CC4.1': [
        ['Evidence of ongoing control monitoring during the period (dashboards, check results, internal reviews)', { checks: true }],
        ['Most recent penetration test report', { checks: true, obligations: true }],
    ],
    'CC4.2': [
        ['Listing of control deficiencies found in the period and their remediation status', { checks: true }],
    ],
    // ---- CC5 Control activities -------------------------------------------
    'CC5.1': [
        ['Mapping of risks to the controls that address them', { policy_keys: ['risk_management'], checks: true }],
    ],
    'CC5.2': [
        ['Evidence of technology general controls over infrastructure and access (configuration baselines, admin access listing)', { checks: true }],
    ],
    'CC5.3': [
        ['Full policy set with approval dates and review history', { policy_keys: ['information_security'], checks: true }],
    ],
    // ---- CC6 Logical and physical access ----------------------------------
    'CC6.1': [
        ['Authentication settings for the identity provider and production systems (MFA enforcement, password rules)', { checks: true }],
        ['Encryption-at-rest configuration for production data stores', { policy_keys: ['encryption_key_management'], checks: true }],
        ['System and asset inventory for in-scope systems', { checks: true }],
    ],
    'CC6.2': [
        ['Listing of new users added during the period with approval evidence', { population_kinds: ['new_hires', 'user_access_changes'], checks: true }],
        ['Listing of users removed during the period with removal dates', { population_kinds: ['terminations'], checks: true }],
    ],
    'CC6.3': [
        ['User access reviews performed in the period, with reviewer sign-off and resulting changes', { population_kinds: ['access_reviews'], checks: true, obligations: true }],
        ['Listing of privileged / administrator accounts and their justification', { policy_keys: ['access_control'], checks: true }],
    ],
    'CC6.4': [
        ['Physical access controls for offices and data centers, or the hosting provider\'s report covering them', { checks: true }],
    ],
    'CC6.5': [
        ['Records of device and media disposal in the period', { policy_keys: ['data_classification_handling'], checks: true }],
    ],
    'CC6.6': [
        ['Network diagrams and firewall / security group rules for the production boundary', { checks: true }],
    ],
    'CC6.7': [
        ['TLS configuration for external endpoints and rules for moving data to third parties or removable media', { policy_keys: ['encryption_key_management', 'data_classification_handling'], checks: true }],
    ],
    'CC6.8': [
        ['Endpoint protection and device-management coverage for company devices', { population_kinds: ['managed_devices'], checks: true }],
        ['Vulnerability and dependency scanning results for the period', { checks: true }],
    ],
    // ---- CC7 System operations --------------------------------------------
    'CC7.1': [
        ['Configuration drift and vulnerability scan results for the period, with remediation tracking', { checks: true }],
    ],
    'CC7.2': [
        ['Logging and alerting configuration, with examples of alerts raised and handled in the period', { policy_keys: ['logging_monitoring'], checks: true }],
    ],
    'CC7.3': [
        ['Listing of security events escalated in the period and how each was triaged', { population_kinds: ['incidents'], checks: true }],
    ],
    'CC7.4': [
        ['Incident response plan and records for incidents in the period', { policy_keys: ['incident_response'], population_kinds: ['incidents'], checks: true }],
        ['Most recent incident response exercise (tabletop) and its follow-up actions', { checks: true, obligations: true }],
    ],
    'CC7.5': [
        ['Post-incident reviews for incidents in the period and recovery evidence', { policy_keys: ['incident_response'], checks: true }],
    ],
    // ---- CC8 Change management ---------------------------------------------
    'CC8.1': [
        ['Listing of production changes deployed during the period', { population_kinds: ['changes'], checks: true }],
        ['Branch protection and required-review settings on production repositories', { policy_keys: ['change_management'], checks: true }],
    ],
    // ---- CC9 Risk mitigation ----------------------------------------------
    'CC9.1': [
        ['Business continuity and disaster recovery plan with the most recent test results', { policy_keys: ['business_continuity_disaster_recovery'], checks: true, obligations: true }],
        ['Insurance coverage summary for business disruption', {}],
    ],
    'CC9.2': [
        ['Vendor inventory with risk tiers, and the reviews performed in the period for higher-risk vendors', { policy_keys: ['vendor_management'], population_kinds: ['vendors'], checks: true, obligations: true }],
    ],
    // ---- A1 Availability ---------------------------------------------------
    'A1.1': [
        ['Capacity and availability monitoring dashboards and alerts for the period', { checks: true }],
    ],
    'A1.2': [
        ['Backup configuration, schedule, and success reports for the period', { policy_keys: ['business_continuity_disaster_recovery'], checks: true }],
    ],
    'A1.3': [
        ['Results of backup restore and recovery tests performed in the period', { checks: true, obligations: true }],
    ],
    // ---- C1 Confidentiality -------------------------------------------------
    'C1.1': [
        ['Data classification scheme and inventory of where confidential data is stored', { policy_keys: ['data_classification_handling'], checks: true }],
    ],
    'C1.2': [
        ['Retention schedule and records of confidential data deleted in the period', { policy_keys: ['data_classification_handling'], checks: true }],
    ],
    // ---- PI1 Processing integrity ------------------------------------------
    'PI1.1': [['Specifications for key processing: inputs, outputs, and quality objectives', { checks: true }]],
    'PI1.2': [['Input validation rules and evidence they operate', { checks: true }]],
    'PI1.3': [['Processing monitoring and error-handling records for the period', { checks: true }]],
    'PI1.4': [['Output review and delivery controls, with examples from the period', { checks: true }]],
    'PI1.5': [['Controls protecting stored processing data from unauthorized change', { checks: true }]],
    // ---- P Privacy ----------------------------------------------------------
    'P1.1': [['Current privacy notice and its change history', { checks: true }]],
    'P2.1': [['How consent is captured and recorded, with examples from the period', { checks: true }]],
    'P3.1': [['Inventory of personal data collected and the purpose for each', { checks: true }]],
    'P3.2': [['Records of explicit consent where it is required', { checks: true }]],
    'P4.1': [['Evidence personal data use is limited to the stated purposes', { checks: true }]],
    'P4.2': [['Retention schedule for personal data', { policy_keys: ['data_classification_handling'], checks: true }]],
    'P4.3': [['Records of personal data disposed of in the period', { checks: true }]],
    'P5.1': [['Listing of data subject access requests in the period and response times', { population_kinds: ['privacy_requests'], checks: true }]],
    'P5.2': [['Listing of correction requests in the period and how each was resolved', { population_kinds: ['privacy_requests'], checks: true }]],
    'P6.1': [['Third parties receiving personal data and the consent basis for each', { policy_keys: ['vendor_management'], checks: true }]],
    'P6.2': [['Record of authorized disclosures of personal data in the period', { checks: true }]],
    'P6.3': [['Record of unauthorized disclosures in the period, if any', { population_kinds: ['incidents'], checks: true }]],
    'P6.4': [['Privacy terms in contracts with vendors that handle personal data', { policy_keys: ['vendor_management'], checks: true }]],
    'P6.5': [['Vendor obligations to notify the company of breaches, and notices received', { policy_keys: ['vendor_management'], checks: true }]],
    'P6.6': [['Breach notification procedure and notifications sent in the period', { policy_keys: ['incident_response'], checks: true }]],
    'P6.7': [['How disclosures are accounted for when a data subject asks', { checks: true }]],
    'P7.1': [['Controls keeping personal data accurate and complete', { checks: true }]],
    'P8.1': [['Listing of privacy inquiries and complaints in the period and their resolution', { population_kinds: ['privacy_requests'], checks: true }]],
};
/** Requests keyed by TSC criterion ref (e.g. 'CC6.2'). */
export const SOC2_PBC_TEMPLATE = Object.fromEntries(Object.entries(RAW).map(([ref, reqs]) => [
    ref,
    reqs.map(([text, sources], i) => ({ key: `${ref}-${i + 1}`, text, sources })),
]));
/** Used for any active SOC 2 requirement the template does not list. */
export function fallbackPbcRequests(ref, title) {
    return [{ key: `${ref}-1`, text: `Evidence that the controls addressing "${title}" operated during the period`, sources: { checks: true } }];
}
//# sourceMappingURL=soc2-pbc-template.js.map